import { FormEvent, useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";
import "./vehicle-stock.css";

type RoleKey = "stand_master" | "manager" | "sales" | "operations" | "user";
type Vehicle = {
  id: string;
  organization_id: string;
  stock_number: string;
  make: string;
  model: string;
  version: string | null;
  registration: string | null;
  first_registration_year: number | null;
  fuel_type: string | null;
  transmission: string | null;
  mileage_km: number;
  color: string | null;
  purchase_price_cents: number | null;
  sale_price_cents: number | null;
  currency_code: string;
  stock_status: string;
  operational_stage: string;
  acquisition_type: string;
  publication_status: string;
  sold_publication_mode: string;
  pause_reason: string | null;
  featured: boolean;
  description: string | null;
  updated_at: string;
};
type ConsignmentOwner = {
  id: string;
  display_name: string;
  tax_id: string | null;
  email: string | null;
  phone: string | null;
};
type ConsignmentAgreement = {
  id: string;
  owner_id: string;
  status: string;
  owner_amount_cents: number;
  commission_type: string;
  commission_value: number | null;
  minimum_sale_price_cents: number | null;
  starts_on: string;
  ends_on: string | null;
  terms: string | null;
  internal_notes: string | null;
};
type VehicleReservation = {
  id: string;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  assigned_user_id: string;
  status: string;
  expires_at: string;
  deposit_cents: number | null;
  currency_code: string;
  notes: string | null;
};
type ReservationRequest = {
  id: string;
  requested_by: string;
  proposed_customer_name: string;
  message: string;
  status: string;
  created_at: string;
};
const stockLabels: Record<string, string> = {
  in_stock: "Em stock",
  reserved: "Reservada",
  sale_in_progress: "Em venda",
  sold: "Vendida",
  delivery: "A entregar",
  delivered: "Entregue",
  paused: "Em pausa",
  archived: "Arquivada",
};
const stageLabels: Record<string, string> = {
  purchase: "Compra",
  transport: "Transporte",
  reception: "Receção",
  checkup: "Verificação",
  preparation: "Preparação",
  workshop: "Oficina",
  inspection: "Inspeção",
  exposure: "Exposição",
};
const acquisitionLabels: Record<string, string> = {
  owned: "Viatura própria",
  consignment: "À consignação",
  trade_in: "Retoma",
};
const publicationLabels: Record<string, string> = {
  draft: "Rascunho",
  published: "Publicada",
  unpublished: "Não publicada",
};
const saleStatuses = new Set([
  "sale_in_progress",
  "sold",
  "delivery",
  "delivered",
]);
const money = (value: number | null, currency: string) =>
  value == null
    ? "—"
    : new Intl.NumberFormat(currency === "BRL" ? "pt-BR" : "pt-PT", {
        style: "currency",
        currency,
      }).format(value / 100);

export function VehicleStock({
  organizationId,
  currencyCode,
  writable,
  roleKey,
}: {
  organizationId: string;
  currencyCode: string;
  writable: boolean;
  roleKey: RoleKey;
}) {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]),
    [loading, setLoading] = useState(true),
    [selected, setSelected] = useState<Vehicle | null>(null),
    [creating, setCreating] = useState(false),
    [status, setStatus] = useState("all"),
    [search, setSearch] = useState(""),
    [message, setMessage] = useState<string | null>(null);
  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("vehicles")
      .select("*")
      .eq("organization_id", organizationId)
      .order("updated_at", { ascending: false });
    if (error) setMessage(error.message);
    setVehicles((data ?? []) as Vehicle[]);
    setLoading(false);
  };
  useEffect(() => {
    void load();
  }, [organizationId]);
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 10000);
    return () => clearTimeout(timer);
  }, [message]);
  const filtered = useMemo(
    () =>
      vehicles.filter(
        (v) =>
          (status === "all" || v.stock_status === status) &&
          `${v.make} ${v.model} ${v.version ?? ""} ${v.registration ?? ""} ${v.stock_number}`
            .toLowerCase()
            .includes(search.toLowerCase()),
      ),
    [vehicles, status, search],
  );
  const counts = useMemo(
    () => ({
      total: vehicles.filter((v) => v.stock_status !== "archived").length,
      published: vehicles.filter((v) => v.publication_status === "published")
        .length,
      preparation: vehicles.filter((v) =>
        ["preparation", "workshop", "inspection"].includes(v.operational_stage),
      ).length,
      sold: vehicles.filter((v) =>
        ["sold", "delivery", "delivered"].includes(v.stock_status),
      ).length,
    }),
    [vehicles],
  );
  return (
    <section className="vehicle-stock">
      <header className="stock-heading">
        <div>
          <p className="eyebrow">Gestão de viaturas</p>
          <h2>Stock</h2>
          <p>
            Estado comercial, etapa operacional e publicação são controlados
            separadamente.
          </p>
        </div>
        {writable && (
          <button className="stock-primary" onClick={() => setCreating(true)}>
            + Adicionar viatura
          </button>
        )}
      </header>
      {message && (
        <button className="stock-notice" onClick={() => setMessage(null)}>
          {message} ×
        </button>
      )}
      <div className="stock-summary">
        <article>
          <small>Stock atual</small>
          <strong>{counts.total}</strong>
        </article>
        <article>
          <small>Publicadas</small>
          <strong>{counts.published}</strong>
        </article>
        <article>
          <small>Em preparação</small>
          <strong>{counts.preparation}</strong>
        </article>
        <article>
          <small>Vendidas</small>
          <strong>{counts.sold}</strong>
        </article>
      </div>
      <div className="stock-filters">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Pesquisar viatura, matrícula ou referência…"
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">Todos os estados</option>
          {Object.entries(stockLabels).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>
      {loading ? (
        <div className="stock-empty">A carregar o stock…</div>
      ) : filtered.length === 0 ? (
        <div className="stock-empty">
          <strong>Não existem viaturas para estes filtros.</strong>
          <span>
            {writable
              ? "Adicione uma viatura ou altere os filtros."
              : "O stand ainda não registou viaturas."}
          </span>
        </div>
      ) : (
        <div className="vehicle-list">
          {filtered.map((v) => (
            <button
              key={v.id}
              className="vehicle-row"
              onClick={() => setSelected(v)}
            >
              <span className="vehicle-ref">{v.stock_number}</span>
              <span className="vehicle-name">
                <strong>
                  {v.make} {v.model}
                </strong>
                <small>
                  {acquisitionLabels[v.acquisition_type]} ·{" "}
                  {stageLabels[v.operational_stage]}
                </small>
              </span>
              <span>
                <strong>{v.first_registration_year ?? "—"}</strong>
                <small>{v.registration ?? "Sem matrícula"}</small>
              </span>
              <span>
                <strong>{v.mileage_km.toLocaleString("pt-PT")} km</strong>
                <small>{v.fuel_type ?? "Combustível não indicado"}</small>
              </span>
              <span className={`stock-status status-${v.stock_status}`}>
                {stockLabels[v.stock_status]}
              </span>
              <span>
                <strong>{money(v.sale_price_cents, v.currency_code)}</strong>
                <small>{publicationLabels[v.publication_status]}</small>
              </span>
            </button>
          ))}
        </div>
      )}
      {(creating || selected) && (
        <VehicleDialog
          vehicle={selected}
          organizationId={organizationId}
          currencyCode={currencyCode}
          writable={writable}
          roleKey={roleKey}
          onClose={() => {
            setCreating(false);
            setSelected(null);
          }}
          onSaved={async () => {
            const edited = Boolean(selected);
            setCreating(false);
            setSelected(null);
            setMessage(
              edited ? "Viatura atualizada." : "Viatura adicionada ao stock.",
            );
            await load();
          }}
        />
      )}
    </section>
  );
}

function VehicleDialog({
  vehicle,
  organizationId,
  currencyCode,
  writable,
  roleKey,
  onClose,
  onSaved,
}: {
  vehicle: Vehicle | null;
  organizationId: string;
  currencyCode: string;
  writable: boolean;
  roleKey: RoleKey;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false),
    [error, setError] = useState<string | null>(null),
    [chosenStatus, setChosenStatus] = useState(
      vehicle?.stock_status ?? "in_stock",
    );
  const [acquisitionType, setAcquisitionType] = useState(
    vehicle?.acquisition_type ?? "owned",
  );
  const [owners, setOwners] = useState<ConsignmentOwner[]>([]);
  const [ownerId, setOwnerId] = useState("__new__");
  const [agreement, setAgreement] = useState<ConsignmentAgreement | null>(null);
  const [consignmentReady, setConsignmentReady] = useState(!vehicle);
  const canManageLifecycle =
    roleKey === "stand_master" || roleKey === "manager";
  useEffect(() => {
    void Promise.all([
      supabase
        .from("consignment_owners")
        .select("id,display_name,tax_id,email,phone")
        .eq("organization_id", organizationId)
        .eq("active", true)
        .order("display_name"),
      vehicle
        ? supabase
            .from("vehicle_consignment_agreements")
            .select(
              "id,owner_id,status,owner_amount_cents,commission_type,commission_value,minimum_sale_price_cents,starts_on,ends_on,terms,internal_notes",
            )
            .eq("vehicle_id", vehicle.id)
            .maybeSingle()
        : Promise.resolve({ data: null, error: null }),
    ]).then(([ownerResult, agreementResult]) => {
      if (ownerResult.error) setError(ownerResult.error.message);
      setOwners((ownerResult.data ?? []) as ConsignmentOwner[]);
      const current = agreementResult.data as ConsignmentAgreement | null;
      setAgreement(current);
      if (current) setOwnerId(current.owner_id);
      setConsignmentReady(true);
    });
  }, [organizationId, vehicle]);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!writable) return;
    setSaving(true);
    setError(null);
    const f = new FormData(event.currentTarget);
    const payload = {
      organization_id: organizationId,
      stock_number: String(f.get("stock_number") ?? "").trim(),
      make: String(f.get("make") ?? "").trim(),
      model: String(f.get("model") ?? "").trim(),
      version: String(f.get("version") || "") || null,
      registration: String(f.get("registration") || "").toUpperCase() || null,
      first_registration_year: f.get("year") ? Number(f.get("year")) : null,
      fuel_type: String(f.get("fuel_type") || "") || null,
      transmission: String(f.get("transmission") || "") || null,
      mileage_km: Number(f.get("mileage") || 0),
      color: String(f.get("color") || "") || null,
      purchase_price_cents: f.get("purchase_price")
        ? Math.round(Number(f.get("purchase_price")) * 100)
        : null,
      sale_price_cents: f.get("sale_price")
        ? Math.round(Number(f.get("sale_price")) * 100)
        : null,
      currency_code: currencyCode,
      acquisition_type: acquisitionType,
      operational_stage: String(f.get("operational_stage")),
      stock_status: String(f.get("stock_status")),
      pause_reason: String(f.get("pause_reason") || "") || null,
      publication_status: String(f.get("publication_status")),
      featured: f.get("featured") === "on",
      description: String(f.get("description") || "") || null,
    };
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const vehicleResult = vehicle
      ? await supabase
          .from("vehicles")
          .update(payload)
          .eq("id", vehicle.id)
          .eq("organization_id", organizationId)
          .select("id")
          .single()
      : await supabase
          .from("vehicles")
          .insert({ ...payload, created_by: user?.id })
          .select("id")
          .single();
    if (vehicleResult.error) {
      setSaving(false);
      setError(vehicleResult.error.message);
      return;
    }
    const vehicleId = vehicleResult.data.id as string;
    if (canManageLifecycle && acquisitionType === "consignment") {
      let selectedOwnerId = ownerId;
      if (selectedOwnerId === "__new__") {
        const ownerResult = await supabase
          .from("consignment_owners")
          .insert({
            organization_id: organizationId,
            owner_type: String(f.get("owner_type")),
            display_name: String(f.get("owner_name") ?? "").trim(),
            tax_id: String(f.get("owner_tax_id") || "") || null,
            email: String(f.get("owner_email") || "") || null,
            phone: String(f.get("owner_phone") || "") || null,
            address: String(f.get("owner_address") || "") || null,
            notes: String(f.get("owner_notes") || "") || null,
            created_by: user?.id,
          })
          .select("id")
          .single();
        if (ownerResult.error) {
          setSaving(false);
          setError(
            `A viatura foi guardada, mas o proprietário não: ${ownerResult.error.message}`,
          );
          return;
        }
        selectedOwnerId = ownerResult.data.id;
      }
      const agreementPayload = {
        organization_id: organizationId,
        vehicle_id: vehicleId,
        owner_id: selectedOwnerId,
        status: String(f.get("agreement_status")),
        owner_amount_cents: Math.round(
          Number(f.get("owner_amount") || 0) * 100,
        ),
        commission_type: String(f.get("commission_type")),
        commission_value: f.get("commission_value")
          ? Number(f.get("commission_value"))
          : null,
        minimum_sale_price_cents: f.get("minimum_sale_price")
          ? Math.round(Number(f.get("minimum_sale_price")) * 100)
          : null,
        currency_code: currencyCode,
        starts_on: String(f.get("starts_on")),
        ends_on: String(f.get("ends_on") || "") || null,
        terms: String(f.get("terms") || "") || null,
        internal_notes: String(f.get("consignment_notes") || "") || null,
        created_by: user?.id,
      };
      const agreementResult = agreement
        ? await supabase
            .from("vehicle_consignment_agreements")
            .update(agreementPayload)
            .eq("id", agreement.id)
        : await supabase
            .from("vehicle_consignment_agreements")
            .insert(agreementPayload);
      if (agreementResult.error) {
        setSaving(false);
        setError(
          `A viatura foi guardada, mas o acordo não: ${agreementResult.error.message}`,
        );
        return;
      }
    } else if (
      canManageLifecycle &&
      agreement &&
      acquisitionType !== "consignment"
    ) {
      const endResult = await supabase
        .from("vehicle_consignment_agreements")
        .update({
          status: "ended",
          ends_on: new Date().toISOString().slice(0, 10),
        })
        .eq("id", agreement.id);
      if (endResult.error) {
        setSaving(false);
        setError(endResult.error.message);
        return;
      }
    }
    setSaving(false);
    onSaved();
  };
  return (
    <div className="stock-dialog-backdrop">
      <section className="stock-dialog" role="dialog" aria-modal="true">
        <header>
          <div>
            <p className="eyebrow">
              {vehicle ? "Ficha da viatura" : "Nova entrada"}
            </p>
            <h2>
              {vehicle
                ? `${vehicle.make} ${vehicle.model}`
                : "Adicionar viatura"}
            </h2>
            <p>
              Registe a origem, acompanhe a etapa física e controle o estado
              comercial sem perder histórico.
            </p>
          </div>
          <button className="stock-close" onClick={onClose}>
            ×
          </button>
        </header>
        <form onSubmit={submit}>
          <fieldset className="stock-section">
            <legend>Entrada e acompanhamento</legend>
            <p className="stock-section-help">
              A origem não muda o estado. A etapa indica onde a viatura está
              fisicamente no processo.
            </p>
            <div className="stock-form-grid">
              <label>
                <span>Tipo de entrada</span>
                <select
                  name="acquisition_type"
                  value={acquisitionType}
                  onChange={(e) => setAcquisitionType(e.target.value)}
                  disabled={!writable || (!!agreement && !canManageLifecycle)}
                >
                  {Object.entries(acquisitionLabels).map(([key, label]) => (
                    <option value={key} key={key} disabled={key === "consignment" && !canManageLifecycle && vehicle?.acquisition_type !== "consignment"}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Etapa operacional</span>
                <select
                  name="operational_stage"
                  defaultValue={vehicle?.operational_stage ?? "reception"}
                  disabled={!writable}
                >
                  {Object.entries(stageLabels).map(([key, label]) => (
                    <option value={key} key={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Estado da viatura</span>
                <select
                  name="stock_status"
                  value={chosenStatus}
                  onChange={(e) => setChosenStatus(e.target.value)}
                  disabled={!writable}
                >
                  {Object.entries(stockLabels).map(([key, label]) => (
                    <option
                      value={key}
                      key={key}
                      disabled={
                        (saleStatuses.has(key) &&
                          key !== vehicle?.stock_status) ||
                        ((key === "paused" || key === "archived") &&
                          !canManageLifecycle)
                      }
                    >
                      {label}
                    </option>
                  ))}
                </select>
                <small>
                  Reservas e vendas serão alteradas pelos respetivos processos.
                </small>
              </label>
              {chosenStatus === "paused" && (
                <label className="wide">
                  <span>Motivo da pausa *</span>
                  <textarea
                    name="pause_reason"
                    rows={2}
                    defaultValue={vehicle?.pause_reason ?? ""}
                    required
                    disabled={!writable}
                  />
                </label>
              )}
            </div>
          </fieldset>
          {vehicle && (
            <ReservationPanel
              vehicle={vehicle}
              organizationId={organizationId}
              currencyCode={currencyCode}
              writable={writable}
              roleKey={roleKey}
            />
          )}
          {acquisitionType === "consignment" && (
            <fieldset className="stock-section consignment-section">
              <legend>Consignação</legend>
              <p className="stock-section-help">
                Identifique o proprietário e as condições acordadas. O mesmo
                proprietário pode ficar associado a várias viaturas.
              </p>
              {!canManageLifecycle && (
                <p className="consignment-permission-note">
                  Apenas o Master ou Gestor pode alterar condições de consignação.
                </p>
              )}
              {!consignmentReady ? (
                <p className="stock-section-help">A carregar o acordo…</p>
              ) : (
                <div className="stock-form-grid">
                  <label className="wide">
                    <span>Proprietário *</span>
                    <select
                      name="owner_id"
                      value={ownerId}
                      onChange={(e) => setOwnerId(e.target.value)}
                      disabled={!writable || !canManageLifecycle}
                    >
                      <option value="__new__">+ Cadastrar novo proprietário</option>
                      {owners.map((owner) => (
                        <option key={owner.id} value={owner.id}>
                          {owner.display_name}{owner.tax_id ? ` · ${owner.tax_id}` : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                  {ownerId === "__new__" && (
                    <>
                      <label>
                        <span>Tipo de proprietário *</span>
                        <select name="owner_type" defaultValue="individual" disabled={!writable || !canManageLifecycle}>
                          <option value="individual">Pessoa singular</option>
                          <option value="company">Empresa</option>
                        </select>
                      </label>
                      <label>
                        <span>Nome do proprietário *</span>
                        <input name="owner_name" required disabled={!writable || !canManageLifecycle}/>
                      </label>
                      <label><span>NIF ou NIPC</span><input name="owner_tax_id" disabled={!writable || !canManageLifecycle}/></label>
                      <label><span>Telefone</span><input name="owner_phone" disabled={!writable || !canManageLifecycle}/></label>
                      <label><span>Email</span><input name="owner_email" type="email" disabled={!writable || !canManageLifecycle}/></label>
                      <label><span>Morada</span><input name="owner_address" disabled={!writable || !canManageLifecycle}/></label>
                      <label className="wide"><span>Notas do proprietário</span><textarea name="owner_notes" rows={2} disabled={!writable || !canManageLifecycle}/></label>
                    </>
                  )}
                  <label>
                    <span>Estado do acordo *</span>
                    <select name="agreement_status" defaultValue={agreement?.status ?? "active"} disabled={!writable || !canManageLifecycle}>
                      <option value="draft">Rascunho</option>
                      <option value="active">Ativo</option>
                      <option value="ended">Terminado</option>
                      <option value="cancelled">Cancelado</option>
                    </select>
                  </label>
                  <label>
                    <span>Valor devido ao proprietário *</span>
                    <input name="owner_amount" type="number" min="0" step="0.01" required defaultValue={agreement ? agreement.owner_amount_cents / 100 : ""} disabled={!writable || !canManageLifecycle}/>
                  </label>
                  <label>
                    <span>Forma de remuneração do stand</span>
                    <select name="commission_type" defaultValue={agreement?.commission_type ?? "margin"} disabled={!writable || !canManageLifecycle}>
                      <option value="margin">Margem entre valor devido e venda</option>
                      <option value="fixed">Comissão fixa</option>
                      <option value="percentage">Comissão percentual</option>
                    </select>
                  </label>
                  <label>
                    <span>Comissão fixa ou percentual</span>
                    <input name="commission_value" type="number" min="0" step="0.01" defaultValue={agreement?.commission_value ?? ""} disabled={!writable || !canManageLifecycle}/>
                  </label>
                  <label>
                    <span>Preço mínimo autorizado</span>
                    <input name="minimum_sale_price" type="number" min="0" step="0.01" defaultValue={agreement?.minimum_sale_price_cents != null ? agreement.minimum_sale_price_cents / 100 : ""} disabled={!writable || !canManageLifecycle}/>
                  </label>
                  <label>
                    <span>Data de início *</span>
                    <input name="starts_on" type="date" required defaultValue={agreement?.starts_on ?? new Date().toISOString().slice(0, 10)} disabled={!writable || !canManageLifecycle}/>
                  </label>
                  <label>
                    <span>Data de fim</span>
                    <input name="ends_on" type="date" defaultValue={agreement?.ends_on ?? ""} disabled={!writable || !canManageLifecycle}/>
                  </label>
                  <label className="wide"><span>Condições acordadas</span><textarea name="terms" rows={3} defaultValue={agreement?.terms ?? ""} disabled={!writable || !canManageLifecycle}/></label>
                  <label className="wide"><span>Notas internas do acordo</span><textarea name="consignment_notes" rows={2} defaultValue={agreement?.internal_notes ?? ""} disabled={!writable || !canManageLifecycle}/></label>
                </div>
              )}
            </fieldset>
          )}
          <fieldset className="stock-section">
            <legend>Identificação</legend>
            <div className="stock-form-grid">
              <label>
                <span>Referência de stock *</span>
                <input
                  name="stock_number"
                  defaultValue={vehicle?.stock_number}
                  required
                  disabled={!writable}
                />
              </label>
              <label>
                <span>Matrícula</span>
                <input
                  name="registration"
                  defaultValue={vehicle?.registration ?? ""}
                  disabled={!writable}
                />
              </label>
              <label>
                <span>Marca *</span>
                <input
                  name="make"
                  defaultValue={vehicle?.make}
                  required
                  disabled={!writable}
                />
              </label>
              <label>
                <span>Modelo *</span>
                <input
                  name="model"
                  defaultValue={vehicle?.model}
                  required
                  disabled={!writable}
                />
              </label>
              <label className="wide">
                <span>Versão</span>
                <input
                  name="version"
                  defaultValue={vehicle?.version ?? ""}
                  disabled={!writable}
                />
              </label>
              <label>
                <span>Ano da primeira matrícula</span>
                <input
                  name="year"
                  type="number"
                  min="1900"
                  max="2100"
                  defaultValue={vehicle?.first_registration_year ?? ""}
                  disabled={!writable}
                />
              </label>
              <label>
                <span>Quilómetros</span>
                <input
                  name="mileage"
                  type="number"
                  min="0"
                  defaultValue={vehicle?.mileage_km ?? 0}
                  disabled={!writable}
                />
              </label>
              <label>
                <span>Combustível</span>
                <select
                  name="fuel_type"
                  defaultValue={vehicle?.fuel_type ?? ""}
                  disabled={!writable}
                >
                  <option value="">Escolher</option>
                  {[
                    "Gasolina",
                    "Diesel",
                    "Híbrido",
                    "Híbrido plug-in",
                    "Elétrico",
                    "GPL",
                  ].map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Transmissão</span>
                <select
                  name="transmission"
                  defaultValue={vehicle?.transmission ?? ""}
                  disabled={!writable}
                >
                  <option value="">Escolher</option>
                  <option>Manual</option>
                  <option>Automática</option>
                </select>
              </label>
              <label>
                <span>Cor</span>
                <input
                  name="color"
                  defaultValue={vehicle?.color ?? ""}
                  disabled={!writable}
                />
              </label>
            </div>
          </fieldset>
          <fieldset className="stock-section">
            <legend>Preço e publicação</legend>
            <div className="stock-form-grid">
              <label>
                <span>Preço de compra</span>
                <input
                  name="purchase_price"
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue={
                    vehicle?.purchase_price_cents != null
                      ? vehicle.purchase_price_cents / 100
                      : ""
                  }
                  disabled={!writable}
                />
              </label>
              <label>
                <span>Preço de venda</span>
                <input
                  name="sale_price"
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue={
                    vehicle?.sale_price_cents != null
                      ? vehicle.sale_price_cents / 100
                      : ""
                  }
                  disabled={!writable}
                />
              </label>
              <label>
                <span>Estado da publicação</span>
                <select
                  name="publication_status"
                  defaultValue={vehicle?.publication_status ?? "draft"}
                  disabled={!writable}
                >
                  {Object.entries(publicationLabels).map(([key, label]) => (
                    <option value={key} key={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="stock-check">
                <input
                  name="featured"
                  type="checkbox"
                  defaultChecked={vehicle?.featured}
                  disabled={!writable}
                />
                <span>Destacar esta viatura</span>
              </label>
              <label className="wide">
                <span>Descrição</span>
                <textarea
                  name="description"
                  rows={4}
                  defaultValue={vehicle?.description ?? ""}
                  disabled={!writable}
                />
              </label>
            </div>
          </fieldset>
          {error && <p className="stock-error">{error}</p>}
          <footer>
            <button type="button" className="stock-secondary" onClick={onClose}>
              {writable ? "Cancelar" : "Fechar"}
            </button>
            {writable && (
              <button className="stock-primary" disabled={saving}>
                {saving ? "A guardar…" : "Guardar viatura"}
              </button>
            )}
          </footer>
        </form>
      </section>
    </div>
  );
}

function ReservationPanel({
  vehicle,
  organizationId,
  currencyCode,
  writable,
  roleKey,
}: {
  vehicle: Vehicle;
  organizationId: string;
  currencyCode: string;
  writable: boolean;
  roleKey: RoleKey;
}) {
  const [reservation, setReservation] = useState<VehicleReservation | null>(null);
  const [requests, setRequests] = useState<ReservationRequest[]>([]);
  const [userId, setUserId] = useState("");
  const [names, setNames] = useState<Record<string, string>>({});
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [deposit, setDeposit] = useState("");
  const [notes, setNotes] = useState("");
  const [requestCustomer, setRequestCustomer] = useState("");
  const [requestMessage, setRequestMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const manager = roleKey === "stand_master" || roleKey === "manager";
  const load = async () => {
    const [reservationResult, userResult] = await Promise.all([
      supabase
        .from("vehicle_reservations")
        .select("id,customer_name,customer_email,customer_phone,assigned_user_id,status,expires_at,deposit_cents,currency_code,notes")
        .eq("vehicle_id", vehicle.id)
        .eq("status", "active")
        .maybeSingle(),
      supabase.auth.getUser(),
    ]);
    const current = reservationResult.data as VehicleReservation | null;
    setReservation(current);
    setUserId(userResult.data.user?.id ?? "");
    if (!current) {
      setRequests([]);
      return;
    }
    const requestResult = await supabase
      .from("vehicle_reservation_requests")
      .select("id,requested_by,proposed_customer_name,message,status,created_at")
      .eq("reservation_id", current.id)
      .order("created_at", { ascending: false });
    const rows = (requestResult.data ?? []) as ReservationRequest[];
    setRequests(rows);
    const ids = [...new Set([current.assigned_user_id, ...rows.map((item) => item.requested_by)])];
    if (ids.length) {
      const profileResult = await supabase.from("profiles").select("user_id,display_name").in("user_id", ids);
      setNames(Object.fromEntries((profileResult.data ?? []).map((profile) => [profile.user_id, profile.display_name ?? "Utilizador"])));
    }
  };
  useEffect(() => {
    void load();
  }, [vehicle.id]);
  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(() => setFeedback(null), 10000);
    return () => clearTimeout(timer);
  }, [feedback]);
  const createReservation = async () => {
    if (!customerName.trim() || !expiresAt) {
      setFeedback("Indique o cliente e a validade da reserva.");
      return;
    }
    setBusy(true);
    const { error } = await supabase.rpc("create_vehicle_reservation", {
      p_vehicle_id: vehicle.id,
      p_customer_name: customerName.trim(),
      p_customer_email: customerEmail.trim(),
      p_customer_phone: customerPhone.trim(),
      p_expires_at: new Date(expiresAt).toISOString(),
      p_deposit_cents: deposit ? Math.round(Number(deposit) * 100) : null,
      p_notes: notes.trim(),
    });
    setBusy(false);
    setFeedback(error ? error.message : "Reserva criada. A viatura ficou bloqueada para este cliente.");
    if (!error) await load();
  };
  const endReservation = async () => {
    if (!reservation) return;
    const reason = window.prompt("Indique o motivo para libertar esta reserva:");
    if (!reason?.trim()) return;
    setBusy(true);
    const { error } = await supabase.rpc("end_vehicle_reservation", {
      p_reservation_id: reservation.id,
      p_action: "released",
      p_reason: reason.trim(),
    });
    setBusy(false);
    setFeedback(error ? error.message : "Reserva libertada e registada no histórico.");
    if (!error) await load();
  };
  const requestRelease = async () => {
    if (!reservation || !requestCustomer.trim() || !requestMessage.trim()) {
      setFeedback("Indique o seu cliente e explique o pedido ao comercial responsável.");
      return;
    }
    setBusy(true);
    const { error } = await supabase.rpc("request_vehicle_reservation_release", {
      p_reservation_id: reservation.id,
      p_customer_name: requestCustomer.trim(),
      p_message: requestMessage.trim(),
    });
    setBusy(false);
    setFeedback(error ? error.message : "Pedido enviado ao responsável pela reserva.");
    if (!error) await load();
  };
  const decide = async (requestId: string, decision: "accepted" | "refused" | "overridden") => {
    const message = window.prompt(decision === "refused" ? "Explique por que mantém a reserva:" : "Mensagem para o comercial que receberá a reserva:");
    if (message === null) return;
    setBusy(true);
    const { error } = await supabase.rpc("decide_vehicle_reservation_request", {
      p_request_id: requestId,
      p_decision: decision,
      p_message: message.trim(),
    });
    setBusy(false);
    setFeedback(error ? error.message : decision === "refused" ? "Pedido recusado." : "Reserva transferida com histórico preservado.");
    if (!error) await load();
  };
  const isOwner = reservation?.assigned_user_id === userId;
  return (
    <fieldset className="stock-section reservation-section">
      <legend>Reserva comercial</legend>
      <p className="stock-section-help">Uma reserva bloqueia a viatura para o cliente e comercial indicados. Outro comercial deve pedir a libertação; Gestor e Master podem intervir.</p>
      {feedback && <button type="button" className="stock-notice" onClick={() => setFeedback(null)}>{feedback} ×</button>}
      {!reservation ? (
        <div className="stock-form-grid">
          <label><span>Cliente *</span><input value={customerName} onChange={(event) => setCustomerName(event.target.value)} disabled={!writable || busy} placeholder="Nome do cliente interessado" /></label>
          <label><span>Validade da reserva *</span><input type="datetime-local" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} disabled={!writable || busy} /></label>
          <label><span>Telefone</span><input value={customerPhone} onChange={(event) => setCustomerPhone(event.target.value)} disabled={!writable || busy} /></label>
          <label><span>Email</span><input type="email" value={customerEmail} onChange={(event) => setCustomerEmail(event.target.value)} disabled={!writable || busy} /></label>
          <label><span>Sinal ({currencyCode})</span><input type="number" min="0" step="0.01" value={deposit} onChange={(event) => setDeposit(event.target.value)} disabled={!writable || busy} /></label>
          <label><span>Notas</span><input value={notes} onChange={(event) => setNotes(event.target.value)} disabled={!writable || busy} /></label>
          {writable && <div className="wide reservation-actions"><button type="button" className="stock-secondary" onClick={createReservation} disabled={busy}>{busy ? "A registar…" : "Reservar viatura"}</button></div>}
        </div>
      ) : (
        <div className="reservation-active">
          <div className="reservation-summary">
            <div><small>Cliente</small><strong>{reservation.customer_name}</strong><span>{reservation.customer_phone || reservation.customer_email || "Contacto não indicado"}</span></div>
            <div><small>Responsável</small><strong>{names[reservation.assigned_user_id] ?? "Comercial"}</strong><span>Até {new Date(reservation.expires_at).toLocaleString("pt-PT")}</span></div>
            <div><small>Sinal</small><strong>{money(reservation.deposit_cents, reservation.currency_code)}</strong><span>{reservation.notes || "Sem notas"}</span></div>
          </div>
          {(isOwner || manager) && writable && <div className="reservation-actions"><button type="button" className="stock-secondary" onClick={endReservation} disabled={busy}>Libertar reserva</button></div>}
          {!isOwner && writable && (
            <div className="reservation-request-form">
              <strong>Tem outro cliente interessado?</strong>
              <input value={requestCustomer} onChange={(event) => setRequestCustomer(event.target.value)} placeholder="Nome do seu cliente" disabled={busy} />
              <textarea value={requestMessage} onChange={(event) => setRequestMessage(event.target.value)} placeholder="Explique o pedido de libertação ao responsável" rows={2} disabled={busy} />
              <button type="button" className="stock-secondary" onClick={requestRelease} disabled={busy}>Pedir libertação</button>
            </div>
          )}
          {requests.length > 0 && <div className="reservation-requests"><h3>Pedidos de libertação</h3>{requests.map((request) => <article key={request.id}><div><strong>{request.proposed_customer_name}</strong><span>{names[request.requested_by] ?? "Comercial"} · {request.message}</span><small>{request.status === "pending" ? "A aguardar decisão" : `Estado: ${request.status}`}</small></div>{request.status === "pending" && (isOwner || manager) && <div className="reservation-actions"><button type="button" onClick={() => decide(request.id, "accepted")} disabled={busy}>Transferir</button><button type="button" onClick={() => decide(request.id, "refused")} disabled={busy}>Manter</button>{manager && !isOwner && <button type="button" onClick={() => decide(request.id, "overridden")} disabled={busy}>Decidir como gestor</button>}</div>}</article>)}</div>}
        </div>
      )}
    </fieldset>
  );
}
